import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable } from 'rxjs';
import { UserClaims } from '@app/enums';
import { RoleSiteClaimsResponse } from '@app/interfaces/auth';
import { RoleEditModel, RoleListItem } from '@app/models/system-settings';
import { IListService } from '@app/services';
import { ModalComponent } from '@app/shared/components';
import { FeedbackService } from './feedback.service';

@Injectable()
export class RoleService implements IListService {
	endpointBase = '/api/role-cf';
	endpointBaseOld = '/api/role';

	constructor(
		private readonly http: HttpClient,
		private readonly dialog: MatDialog,
		private readonly feedbackService: FeedbackService
	) {}

	listSiteClaims(): Observable<RoleSiteClaimsResponse> {
		return this.http.get<RoleSiteClaimsResponse>(`${this.endpointBaseOld}/siteClaims`);
	}

	list(): Observable<any> {
		return this.http.get(`${this.endpointBaseOld}/list`);
	}

	public getRoleList(): Observable<any> {
		return this.http.get(`${this.endpointBaseOld}/list-users`);
	}

	// IListService methods implementation
	getList(): Observable<RoleListItem[]> {
		return this.http.get<RoleListItem[]>(`${this.endpointBase}/list`);
	}

	getEntity(Id: string): Observable<RoleEditModel> {
		return this.http.get<RoleEditModel>(`${this.endpointBase}/${Id}`);
	}

	addEntity(role: RoleEditModel): Observable<string> {
		return this.http.put<string>(this.endpointBase, role);
	}

	updateEntity(role: RoleEditModel): Observable<string> {
		return this.http.post<string>(this.endpointBase, role);
	}

	// IListService methods implementation
	deleteEntity(id: string): Observable<void> {
		return this.http.delete<void>(this.endpointBase, { params: { id } });
	}

	// IListService methods implementation
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	bulkDeleteEntities?(ids: number[] | string[]): Observable<any> {
		return EMPTY;
	}

	/**
	 * Determines if the given role can be deleted by checking the following conditions:
	 * 1. The role has a valid Id.
	 * 2. The role is not a global admin.
	 * 3. The role has no associated users.
	 * @param role The role to check for deletability.
	 * @returns True if the role can be deleted, false otherwise.
	 */
	canDeleteEntity(role: RoleListItem | RoleEditModel): boolean {
		if (!role?.Id || role.Name === UserClaims.globalAdmin) {
			return false;
		}
		if ('UserCount' in role && role.UserCount > 0) {
			return false;
		}
		if ('RoleUsers' in role && role.RoleUsers.length > 0) {
			return false;
		}
		return true;
	}

	/**
	 * Deletes the given role after prompting the user for confirmation.
	 * @param role The role to delete.
	 * @param onDeleteSuccessAction An action to perform after successful deletion.
	 */
	deleteEntityWithPrompt(role: RoleListItem | RoleEditModel, onDeleteSuccessAction: () => void): void {
		if (!this.canDeleteEntity(role)) {
			return;
		}

		this.dialog
			.open(ModalComponent, {
				maxWidth: '800px',
				data: {
					title: 'Confirm Delete',
					message: `Are you sure you want to delete the ${role.Name} role?`,
				},
			})
			.afterClosed()
			.subscribe((dialogResult) => {
				if (dialogResult) {
					this.deleteEntity(role.Id).subscribe(() => {
						this.feedbackService.success('deleted', role.Name);
						onDeleteSuccessAction();
					});
				}
			});
	}
}
